import { useState, useCallback, useMemo } from 'react';

import { useMerchant } from 'hooks/context/merchant';
import { NewCartItem, CartItem } from 'hooks/context/cart';
import { Modifier, Item } from 'olo-service/models';
import { useMerchantState } from 'hooks/context/MerchantState';

export function useMenuItemModalState<I extends NewCartItem | CartItem>(menuItem: Item, cartItem?: I) {
  const { isLoading } = useMerchant();
  const { isAcceptingOrders } = useMerchantState();

  const [state, setCartItem] = useState<NewCartItem & Partial<CartItem>>({
    ...cartItem,
    id: menuItem.id,
    name: menuItem.name,
    price: menuItem.price,
    quantity: cartItem?.quantity ?? 1,
    modifiers: cartItem?.modifiers ?? [],
    note: cartItem?.note,
  });

  const setQuantity = useCallback((quantity: number) => setCartItem(prev => ({ ...prev, quantity })), []);

  const setModifiers = useCallback((modifiers: ReadonlyArray<Modifier>) => {
    setCartItem(prev => ({ ...prev, modifiers: modifiers as Modifier[] }));
  }, []);

  const setNote = useCallback((note: string) => setCartItem(prev => ({ ...prev, note })), []);

  const cost = useMemo(() => {
    return state.quantity * (menuItem.price + state.modifiers.reduce((total, mod) => total + mod.price, 0));
  }, [menuItem.price, state.modifiers, state.quantity]);

  return {
    ...state,
    setQuantity,
    setModifiers,
    setNote,
    isAcceptingOrders: !isLoading && isAcceptingOrders,
    cost,
  };
}
